import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/config';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // Extract payment details from URL
      const razorpay_payment_id = searchParams.get('razorpay_payment_id');
      const razorpay_order_id = searchParams.get('razorpay_order_id');
      const razorpay_signature = searchParams.get('razorpay_signature');

      // Check if all required parameters are present
      if (!razorpay_payment_id || !razorpay_order_id || !razorpay_signature) {
        setStatus('failed');
        setMessage('Payment verification failed. Missing payment details.');
        setTimeout(() => navigate('/courses'), 5000);
        return;
      }

      // Verify payment on backend
      const verifyResponse = await fetch(`${API_BASE_URL}/verify-course-payment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_order_id,
          razorpay_payment_id,
          razorpay_signature
        })
      });
      
      const verifyData = await verifyResponse.json();
      
      if (verifyData.success) {
        setStatus('success');
        setMessage('Payment successful! Redirecting to your courses...');
        setTimeout(() => navigate('/my-courses'), 3000);
      } else {
        setStatus('failed');
        setMessage('Payment verification failed. Your payment is safe and will be processed automatically. Please check "My Courses" in a few minutes or contact support.');
        setTimeout(() => navigate('/courses'), 5000);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('failed');
      setMessage('Payment verification failed. Your payment is safe and will be processed automatically. Please check "My Courses" in a few minutes or contact support.');
      setTimeout(() => navigate('/courses'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'verifying' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full">
                <FiLoader className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
                <FiCheckCircle className="w-10 h-10 text-green-600" />
              </div>
            )}
            {status === 'failed' && (
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full">
                <FiXCircle className="w-10 h-10 text-red-600" />
              </div>
            )}
          </div>

          {/* Status Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {status === 'verifying' && 'Verifying Payment'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Verification Failed'}
          </h2>

          {/* Status Message */}
          <p className="text-gray-600 mb-6">
            {message}
          </p>

          {/* Action Buttons */}
          {status === 'success' && (
            <button
              onClick={() => navigate('/my-courses')}
              className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Go to My Courses
            </button>
          )}

          {status === 'failed' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/courses')}
                className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
              >
                Browse Courses
              </button>
              <button
                onClick={() => navigate('/my-courses')}
                className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Check My Courses
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
